import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { authAPI, getErrorMessage } from '../../utils/api';

export default function EmailVerification() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [timer, setTimer] = useState(0);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [checking, setChecking] = useState(false);

  // Get email from localStorage or redirect if not available
  useEffect(() => {
    const storedEmail = localStorage.getItem('pendingEmailVerification');
    if (!storedEmail) {
      navigate('/auth/login');
      return;
    }
    setEmail(storedEmail);
    checkVerificationStatus();
  }, [navigate]);

  // Timer for resend button
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => setTimer(timer - 1), 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  // Poll for verification status every 5 seconds
  useEffect(() => {
    const pollInterval = setInterval(() => {
      checkVerificationStatus();
    }, 5000);
    return () => clearInterval(pollInterval);
  }, [navigate]);

  const checkVerificationStatus = async () => {
    if (checking) return;
    setChecking(true);
    try {
      const response = await authAPI.getVerificationStatus();
      setVerificationStatus(response.data);
      if (response.data.email_verified) {
        toast.success('✓ Email verified! Redirecting to dashboard...');
        setTimeout(() => {
          navigate('/user/dashboard');
        }, 1500);
      }
    } catch (error) {
      // Only log unexpected errors, 401 is expected if not logged in
      if (error.response?.status !== 401) {
        console.error('Error checking verification status:', error);
      }
    } finally {
      setChecking(false);
    }
  };

  const handleResendLink = async () => {
    setResendLoading(true);
    try {
      await authAPI.resendEmailLink({ email });
      toast.success('Verification link sent to your email');
      setTimer(60); // 60 second cooldown
    } catch (error) {
      console.error('Resend error:', error);
      toast.error(getErrorMessage(error, 'Failed to resend verification link'));
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex-col gap-5 bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4 md:p-6">
      <div className="flex flex-col items-center justify-center mb-4">
        <img src="/images/logo.png" alt="Logo" className="h-8 md:h-10 lg:h-12" />
      </div>
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2 p-4 md:p-6">
          <CardTitle className="text-xl md:text-2xl text-center">Verify Your Email</CardTitle>
          <CardDescription className="text-center text-sm md:text-base">
            We've sent a verification link to<br />
            <span className="font-medium text-foreground">{email}</span>
          </CardDescription>
        </CardHeader>

        <CardContent className="p-4 md:p-6">
          <div className="space-y-6">
            <div className="text-center py-4">
              <div className="text-4xl md:text-5xl mb-4">📧</div>
              <p className="text-base md:text-lg font-medium mb-2">Check your email</p>
              <p className="text-xs md:text-sm text-muted-foreground">
                Click the verification link in the email we sent to verify your account.
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                The link will expire in 24 hours
              </p>
            </div>

            {verificationStatus && !verificationStatus.email_verified && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  📧 Please verify your email to access all features
                </p>
              </div>
            )}

            <div className="pt-4 border-t">
              <p className="text-sm text-center text-muted-foreground mb-3">
                Didn't receive the email?
              </p>
              <Button
                type="button"
                className="w-full h-12 bg-[#ff2d2d] hover:bg-[#7f1d1d] text-white font-semibold rounded-none shadow-xl transition-all duration-300 transition-ease-in-out"
                onClick={handleResendLink}
                disabled={resendLoading || timer > 0}
              >
                {timer > 0
                  ? `Resend in ${timer}s`
                  : resendLoading
                    ? 'Sending...'
                    : 'Resend Verification Link'}
              </Button>
            </div>

            <div className="mt-6 p-4 bg-slate-50 rounded-lg space-y-2">
              <p className="text-xs font-medium text-slate-600">Need help?</p>
              <p className="text-xs text-slate-500">
                If you don't see the email, check your spam folder or contact support.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
